// covered-by: frontends/carrie/src/ask-v2/rail/__tests__/AskV2MemoryScopeIcon.test.tsx
import { Layout, Note } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";

import { type AskV2RailMemoryScope } from "./types";

import { ChatIcon } from "../../components/icons/ChatIcon";

/** Stable ids for revert-kill tests — must stay aligned with Figma context-pane memories. */
export const ASK_V2_MEMORY_SCOPE_ICON_KIND = {
  chat: "chat-bubbles",
  project: "project-layout",
  enterprise: "enterprise-note",
} as const satisfies Record<AskV2RailMemoryScope | "chat", string>;

const RAIL_MEMORY_ICON_CLASS = "mt-0.5 shrink-0";
const RAIL_MEMORY_STROKE = "rgb(var(--puntt-neutral-gray-10))";
const RAIL_MEMORY_SURFACE = "var(--ask-puntt-surface-bg)";

type AskV2MemoryScopeIconProps = {
  scope: AskV2RailMemoryScope | "chat";
};

/**
 * Context-pane memory row glyph. Matches the three-tier design (chat / project /
 * enterprise) using the same custom nav icons where applicable.
 */
export function AskV2MemoryScopeIcon({ scope }: AskV2MemoryScopeIconProps) {
  const { t } = useTranslation("ui");
  const kind = ASK_V2_MEMORY_SCOPE_ICON_KIND[scope];
  const testId = `ask-v2-rail-memory-scope-icon-${scope}`;

  if (scope === "chat") {
    return (
      <span
        role="img"
        aria-label={t("askV2.rail.memoryChat")}
        data-testid={testId}
        data-memory-icon-kind={kind}
        className={RAIL_MEMORY_ICON_CLASS}
      >
        <ChatIcon
          size={16}
          color={RAIL_MEMORY_STROKE}
          bg={RAIL_MEMORY_SURFACE}
          hover={false}
        />
      </span>
    );
  }

  if (scope === "enterprise") {
    return (
      <Note
        size={16}
        weight="regular"
        className={`${RAIL_MEMORY_ICON_CLASS} text-puntt-neutral-gray-10`}
        data-testid={testId}
        data-memory-icon-kind={kind}
        aria-label={t("askV2.rail.memoryEnterprise")}
      />
    );
  }

  return (
    <Layout
      size={16}
      weight="regular"
      className={`${RAIL_MEMORY_ICON_CLASS} text-puntt-neutral-gray-10`}
      data-testid={testId}
      data-memory-icon-kind={kind}
      aria-label={t("askV2.rail.memoryProject")}
    />
  );
}
